import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CategoryIcon from '@mui/icons-material/Category';

const MonitoramentoEquipamentos: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getEquipamentos(
          municipioAtivo.id,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de equipamentos.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id]);

  // Calcular KPIs a partir do inventario
  const { totalEquip, totalAtivos, totalInativos, totalTipos } = useMemo(() => {
    const inventario: any[] = data?.inventario || [];
    let total = 0;
    let ativos = 0;
    let inativos = 0;

    inventario.forEach((item: any) => {
      total += Number(item.total || 0);
      ativos += Number(item.ativos || 0);
      inativos += Number(item.inativos || 0);
    });

    return {
      totalEquip: total,
      totalAtivos: ativos,
      totalInativos: inativos,
      totalTipos: inventario.length,
    };
  }, [data?.inventario]);

  const inventario: any[] = useMemo(() => data?.inventario || [], [data]);
  const produtividade: any[] = data?.produtividade || [];

  // Dados para o BarChart: ativos vs inativos por tipo_equipamento
  const chartData = useMemo(() => {
    return inventario.map((item: any) => ({
      tipo_equipamento: item.tipo_equipamento,
      ativos: Number(item.ativos || 0),
      inativos: Number(item.inativos || 0),
    }));
  }, [inventario]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de equipamentos.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Equipamentos
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          {municipioAtivo.nome}
        </Typography>
      </Box>

      {/* KPIs calculados do inventario */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Total de Equipamentos"
            valor={totalEquip.toLocaleString('pt-BR')}
            icone={<PrecisionManufacturingIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Ativos"
            valor={totalAtivos.toLocaleString('pt-BR')}
            icone={<CheckCircleIcon />}
            cor="#27AE60"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Inativos"
            valor={totalInativos.toLocaleString('pt-BR')}
            icone={<WarningAmberIcon />}
            cor="#E74C3C"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Tipos de Equipamento"
            valor={totalTipos.toLocaleString('pt-BR')}
            icone={<CategoryIcon />}
            cor="#2D7D9A"
          />
        </Grid>
      </Grid>

      {/* Cards do inventario */}
      {inventario.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Visao Geral por Tipo
          </Typography>
          <Grid container spacing={2}>
            {inventario.map((item: any, index: number) => {
              const total = Number(item.total || 0);
              const ativos = Number(item.ativos || 0);
              const inativos = Number(item.inativos || 0);
              const pctAtivo = total > 0 ? ((ativos / total) * 100).toFixed(0) : '0';
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: `4px solid ${inativos > 0 ? '#E74C3C' : '#27AE60'}`,
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {item.tipo_equipamento}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Total: {total} | Fabricantes: {Number(item.fabricantes || 0)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={`${ativos} ativos`}
                        size="small"
                        sx={{ fontWeight: 600, backgroundColor: '#E8F5E9', color: '#27AE60' }}
                      />
                      {inativos > 0 && (
                        <Chip
                          label={`${inativos} inativos`}
                          size="small"
                          sx={{ fontWeight: 600, backgroundColor: '#FFEBEE', color: '#E74C3C' }}
                        />
                      )}
                      <Chip
                        label={`${pctAtivo}%`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* BarChart: ativos vs inativos por tipo_equipamento */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Ativos vs Inativos por Tipo
        </Typography>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(chartData.length * 45, 300)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
              <YAxis
                type="category"
                dataKey="tipo_equipamento"
                width={180}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  Number(value).toLocaleString('pt-BR'),
                  name === 'ativos' ? 'Ativos' : 'Inativos',
                ]}
              />
              <Legend
                formatter={(value: string) => value === 'ativos' ? 'Ativos' : 'Inativos'}
              />
              <Bar dataKey="ativos" fill="#27AE60" radius={[0, 4, 4, 0]} stackId="equip" name="ativos" />
              <Bar dataKey="inativos" fill="#E74C3C" radius={[0, 4, 4, 0]} stackId="equip" name="inativos" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de equipamentos para o grafico.
          </Typography>
        )}
      </Paper>

      {/* Tabela do inventario */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Inventario Completo
        </Typography>
        {inventario.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo de Equipamento</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ativos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Inativos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Fabricantes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventario.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.tipo_equipamento}</TableCell>
                    <TableCell align="right">{Number(row.total || 0).toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">
                      <Typography component="span" sx={{ color: '#27AE60', fontWeight: 500 }}>
                        {Number(row.ativos || 0).toLocaleString('pt-BR')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography component="span" sx={{ color: Number(row.inativos || 0) > 0 ? '#E74C3C' : 'text.secondary', fontWeight: 500 }}>
                        {Number(row.inativos || 0).toLocaleString('pt-BR')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{Number(row.fabricantes || 0).toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de inventario disponiveis.
          </Typography>
        )}
      </Paper>

      {/* Produtividade (se houver dados) */}
      {produtividade.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Produtividade
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {Object.keys(produtividade[0] || {}).map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 600 }}>
                      {col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {produtividade.map((row: any, index: number) => (
                  <TableRow key={index}>
                    {Object.values(row).map((val: any, colIdx: number) => (
                      <TableCell key={colIdx}>
                        {val != null ? String(val) : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="equipamentos"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoEquipamentos;
